// RSS COMMERCE — package selection state machine + mobile fixed summary.
//
// Drives the Homepage `rss-home-commerce` package grid (and, later, the
// Store's `rss-package-grid` — this module is written to be reused by
// both, see docs/rss-r1/05-SHARED-COMPONENTS.md). Radiogroup semantics
// (role="radio"/aria-checked + roving tabindex + arrow-key navigation),
// per docs/rss-r1/10-ACCESSIBILITY-SPEC.md.
//
// All pricing math comes from the single canonical engine in
// rss-calculator.js (`window.RSSPricing.computeOrder`) — this file only
// renders state, it never re-derives total/discount/split itself. See
// docs/rss-r1/06-PRICING-ENGINE-SPEC.md.
(function () {
  'use strict';
  if (typeof window === 'undefined' || !window.RSSPricing) return;
  if (window.__rssSelectionLoaded) return;
  window.__rssSelectionLoaded = true;

  var computeOrder = window.RSSPricing.computeOrder;
  // Shared with assets/rss-calculator.js — one USD formatting convention
  // for every RSS pricing surface (package cards, calculator, mobile
  // summary bar). See snippets/rss-format-usd.liquid for the matching
  // server-rendered version.
  var formatMoney = window.RSSPricing.formatUSD;

  function fmtB(n) {
    if (Number.isInteger(n)) return n.toLocaleString('en-US') + 'B';
    return n.toFixed(1) + 'B';
  }

  function totalDisplay(qty) {
    if (qty >= 1000) {
      var t = qty / 1000;
      return (Number.isInteger(t) ? t : t.toFixed(1)) + 'T';
    }
    return fmtB(qty);
  }

  function initSelectionRoot(root) {
    var grid = root.querySelector('[data-rss-package-grid]');
    if (!grid) return;

    var cards = Array.prototype.slice.call(grid.querySelectorAll('[data-rss-package]'));
    if (!cards.length) return;

    var continueEls = root.querySelectorAll('[data-rss-continue]');
    var hint = root.querySelector('[data-rss-order-hint]');
    var liveRegion = root.querySelector('[data-rss-order-live]');
    var continueUrl = root.dataset.continueUrl || '#';

    var summary = root.querySelector('[data-rss-mobile-summary]');
    var summaryExpand = summary && summary.querySelector('[data-rss-summary-expand]');
    var summarySplit = summary && summary.querySelector('[data-rss-summary-split]');
    var summaryClear = summary && summary.querySelector('[data-rss-summary-clear]');

    function cardOrder(card) {
      return computeOrder(parseFloat(card.dataset.packageTotal));
    }

    // Reserve the exact rendered height of the fixed mobile bar as page
    // bottom padding so the footer/last section content is never hidden
    // behind it — measured from the one live element (ResizeObserver),
    // never a hardcoded per-breakpoint guess. Removed with the bar itself.
    function syncSpacer() {
      if (!summary) return;
      if (summary.hidden) {
        document.body.style.removeProperty('--rss-mobile-summary-reserve');
        return;
      }
      var height = summary.getBoundingClientRect().height;
      document.body.style.setProperty('--rss-mobile-summary-reserve', height + 'px');
    }

    if (summary && typeof ResizeObserver !== 'undefined') {
      new ResizeObserver(syncSpacer).observe(summary);
    }

    // Some CONTINUE/GET RSS targets (the Final Commerce CTA) wrap an icon
    // alongside the label — setting textContent directly on the element
    // would blow away that icon. Prefer a nested [data-rss-continue-label]
    // when present; plain-text targets (mobile summary bar) fall back to
    // textContent on the element itself, unchanged from before.
    function setContinueLabel(el, text) {
      var label = el.querySelector('[data-rss-continue-label]');
      if (label) {
        label.textContent = text;
      } else {
        el.textContent = text;
      }
    }

    function setRovingTabindex(card) {
      cards.forEach(function (c) {
        c.tabIndex = c === card ? 0 : -1;
      });
    }

    function updateSelectedTag(card) {
      var tag = card.querySelector('[data-rss-selected-tag]');
      if (tag) tag.hidden = card.getAttribute('aria-checked') !== 'true';
    }

    function select(card, opts) {
      var moveFocus = !opts || opts.moveFocus !== false;
      var announce = !opts || opts.announce !== false;

      cards.forEach(function (c) {
        c.setAttribute('aria-checked', c === card ? 'true' : 'false');
        updateSelectedTag(c);
      });
      setRovingTabindex(card);
      if (moveFocus) card.focus();

      var order = cardOrder(card);
      var display = totalDisplay(order.qty);

      continueEls.forEach(function (el) {
        setContinueLabel(el, 'CONTINUE');
        el.href = continueUrl;
        el.removeAttribute('aria-disabled');
      });
      if (hint) hint.hidden = true;

      if (summary) {
        summary.hidden = false;
        var pkgEl = summary.querySelector('[data-rss-summary-package]');
        var totalEl = summary.querySelector('[data-rss-summary-total]');
        var effEl = summary.querySelector('[data-rss-summary-effective]');
        var savEl = summary.querySelector('[data-rss-summary-savings]');
        var foodEl = summary.querySelector('[data-rss-split-food]');
        var lumberEl = summary.querySelector('[data-rss-split-lumber]');
        var stoneEl = summary.querySelector('[data-rss-split-stone]');
        var oreEl = summary.querySelector('[data-rss-split-ore]');

        if (pkgEl) pkgEl.textContent = display + ' RSS';
        if (totalEl) totalEl.textContent = formatMoney(order.total);
        if (effEl) effEl.textContent = formatMoney(order.effective, true) + ' / 1B';
        if (savEl) savEl.textContent = formatMoney(order.discount);
        var eachText = fmtB(order.each);
        if (foodEl) foodEl.textContent = eachText;
        if (lumberEl) lumberEl.textContent = eachText;
        if (stoneEl) stoneEl.textContent = eachText;
        if (oreEl) oreEl.textContent = eachText;

        requestAnimationFrame(syncSpacer);
      }

      if (announce && liveRegion) {
        liveRegion.textContent = display + ' RSS selected — ' + formatMoney(order.total) + ' total, save ' + formatMoney(order.discount) + '.';
      }
    }

    function clear(opts) {
      var moveFocus = !opts || opts.moveFocus !== false;
      cards.forEach(function (c) {
        c.setAttribute('aria-checked', 'false');
        updateSelectedTag(c);
      });
      setRovingTabindex(cards[0]);
      if (moveFocus) cards[0].focus();

      continueEls.forEach(function (el) {
        setContinueLabel(el, 'GET RSS');
        el.setAttribute('aria-disabled', 'true');
        el.setAttribute('href', '#');
      });
      if (hint) hint.hidden = false;

      if (summary) {
        summary.hidden = true;
        if (summarySplit) {
          summarySplit.hidden = true;
        }
        if (summaryExpand) summaryExpand.setAttribute('aria-expanded', 'false');
        syncSpacer();
      }

      if (liveRegion) liveRegion.textContent = 'Selection cleared.';
    }

    cards.forEach(function (card, index) {
      card.addEventListener('click', function () {
        select(card, { moveFocus: false });
      });

      card.addEventListener('keydown', function (e) {
        var targetIndex = null;
        if (e.key === 'ArrowRight' || e.key === 'ArrowDown') {
          targetIndex = (index + 1) % cards.length;
        } else if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') {
          targetIndex = (index - 1 + cards.length) % cards.length;
        } else if (e.key === 'Home') {
          targetIndex = 0;
        } else if (e.key === 'End') {
          targetIndex = cards.length - 1;
        }
        if (targetIndex !== null) {
          e.preventDefault();
          select(cards[targetIndex]);
        }
      });
    });

    continueEls.forEach(function (el) {
      el.addEventListener('click', function (e) {
        if (el.getAttribute('aria-disabled') === 'true') e.preventDefault();
      });
    });

    if (summaryExpand && summarySplit) {
      summaryExpand.addEventListener('click', function () {
        var expanded = summaryExpand.getAttribute('aria-expanded') === 'true';
        summaryExpand.setAttribute('aria-expanded', String(!expanded));
        summarySplit.hidden = expanded;
        requestAnimationFrame(syncSpacer);
      });
    }

    if (summaryClear) {
      summaryClear.addEventListener('click', function () {
        clear({ moveFocus: false });
      });
    }

    // Roving tabindex starts at whichever card the server already marked
    // checked (STORE-HOTFIX-02's Store-only default-selection contract —
    // aria-checked="true" rendered server-side; see
    // snippets/rss-store-package-card.liquid's `selected` param), so a
    // pre-selected card doesn't lose keyboard reachability to cards[0] on
    // init. Nothing else in this function changes: aria-checked itself is
    // never touched here, only read, and the visual "selected" look
    // already comes from CSS keyed off [aria-checked="true"] — this line
    // only fixes which card Tab lands on first. Homepage never renders a
    // card pre-checked, so `checkedOnLoad` is always undefined there and
    // this falls back to the original cards[0] behavior, unchanged.
    var checkedOnLoad = cards.filter(function (c) {
      return c.getAttribute('aria-checked') === 'true';
    })[0];
    setRovingTabindex(checkedOnLoad || cards[0]);
  }

  function init() {
    document.querySelectorAll('[data-rss-selection-root]').forEach(initSelectionRoot);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
